import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useCalculos } from "@/hooks/useCalculos";
import { CalculoINSSResult } from "@/types/calculos";
import { addToHistory, useCalculoPrefill } from "@/hooks/useCalculoHistory";

export const CalculosINSS = () => {
  const [valorPago, setValorPago] = useState("");
  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const [resultado, setResultado] = useState<CalculoINSSResult | null>(null);
  
  const { calcularINSS, loading } = useCalculos();

  useCalculoPrefill("inss", (v) => {
    setValorPago(v.valorPago ?? "");
    setDataInicial(v.dataInicial ?? "");
    setDataFinal(v.dataFinal ?? "");
    setResultado(null);
  });

  const handleCalcular = async () => {
    if (!valorPago || !dataInicial || !dataFinal) {
      return;
    }

    try {
      const result = await calcularINSS({
        valorPago: parseFloat(valorPago),
        dataInicial,
        dataFinal
      });
      setResultado(result);
      addToHistory({
        type: "inss",
        label: "Restituição INSS",
        values: { valorPago, dataInicial, dataFinal },
        summary: `Pago ${formatCurrency(parseFloat(valorPago))} • Corrigido ${formatCurrency(result.valorCorrigido)}`,
      });
    } catch (error) {
      console.error("Erro no cálculo:", error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Restituição INSS</CardTitle>
        <p className="text-sm text-muted-foreground">
          Correção de valores pagos indevidamente ao INSS
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="valor-pago">Valor Pago Indevidamente</Label>
            <Input
              id="valor-pago"
              type="number"
              placeholder="2000.00"
              value={valorPago}
              onChange={(e) => setValorPago(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="data-inicial">Data Inicial</Label>
            <Input
              id="data-inicial"
              type="date"
              value={dataInicial}
              onChange={(e) => setDataInicial(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="data-final">Data Final</Label>
            <Input
              id="data-final"
              type="date"
              value={dataFinal}
              onChange={(e) => setDataFinal(e.target.value)}
            />
          </div>
        </div>
        
        <Button 
          onClick={handleCalcular} 
          className="w-full"
          disabled={loading}
        >
          {loading ? "Calculando..." : "Calcular Restituição"}
        </Button>
        
        {resultado && (
          <div className="mt-4 p-4 bg-muted rounded-lg space-y-2">
            <h3 className="font-semibold mb-2">Valor a Restituir:</h3>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(resultado.valorCorrigido)}
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Valor original: {formatCurrency(parseFloat(valorPago))}
              </p>
              <p className="text-sm text-muted-foreground">
                Correção: {formatCurrency(resultado.diferenca)}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};